import { faTrashAlt, faCopy, faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type CellContext } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { reservationMessages } from 'src/features/reservations/utils/messages';
import { disableDeleteButton, disableEditButton } from 'src/features/reservations/utils/utils';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/utils/messages';

import type { ReservationType } from 'src/features/reservations/types/reservationType';

interface CellReservationVersionActionsProps<TData, TValue> extends CellContext<TData, TValue> {
    deleteCallback: (version: TData) => void;
    editCallback: (version: TData) => void;
    copyCallback?: (version: TData) => void;
}
function CellReservationVersionActions({
    deleteCallback,
    editCallback,
    copyCallback,
    row,
}: CellReservationVersionActionsProps<ReservationType, unknown>) {
    const { formatMessage } = useIntl();

    return (
        <div className="ml-auto flex max-w-24 gap-1">
            {copyCallback && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button onClick={() => copyCallback(row.original)} size="icon" variant="link">
                                <FontAwesomeIcon icon={faCopy} />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {`${formatMessage(reservationMessages.copy)} ${formatMessage(reservationMessages.version).toLocaleLowerCase()}`}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            onClick={() => editCallback(row.original)}
                            size="icon"
                            variant="link"
                            disabled={disableEditButton(row.original)}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        {`${formatMessage(generalMessages.edit)} ${formatMessage(reservationMessages.version).toLocaleLowerCase()}`}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            onClick={() => deleteCallback(row.original)}
                            size="icon"
                            variant="link"
                            disabled={disableDeleteButton(row.original)}
                        >
                            <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        {`${formatMessage(generalMessages.delete)} ${formatMessage(reservationMessages.version).toLocaleLowerCase()}`}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}

export { CellReservationVersionActions };
